/**
 * Seção 'Recursos' do detalhe do cliente: tabela compacta (Nome | Tipo | Grupo
 * | Serial | Garantia | Status) filtrada pelo cliente + atalho '+ Recurso'
 * (drawer com o cliente preselecionado) + gestão de grupos do cliente.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Plus } from 'lucide-react';
import type { Equipment, EquipmentDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { equipmentListOptions, errorMessage } from './resources-queries';
import { EquipmentStatusBadge, EquipmentWarranty } from './resources-format';
import { ResourceDrawer } from './resource-drawer';
import { ResourceGroupsDialog } from './resource-groups-dialog';
import { ResourceDetailDialog } from './resource-detail-dialog';

type DrawerState = { mode: 'new' } | { mode: 'edit'; equipment: Equipment };

export function ClientResourcesSection({
  clientId,
  clientName,
}: {
  clientId: number;
  clientName: string;
}) {
  const listQ = useQuery(equipmentListOptions({ client_ids: [clientId], limit: 200 }));
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Recursos do cliente</h2>
          {listQ.data ? (
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
              {listQ.data.total}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setGroupsOpen(true)}>
            <Boxes />
            Grupos de recursos
          </Button>
          <Button size="sm" onClick={() => setDrawer({ mode: 'new' })}>
            <Plus />
            Recurso
          </Button>
        </div>
      </div>
      {listQ.isPending ? (
        <div aria-busy className="space-y-3 px-4 pb-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/5" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-5 w-16 rounded-sm" />
            </div>
          ))}
        </div>
      ) : listQ.isError ? (
        <p className="px-4 pb-4 text-sm text-destructive">
          Erro ao carregar recursos: {errorMessage(listQ.error)}
        </p>
      ) : listQ.data.items.length === 0 ? (
        <EmptyState className="h-32" message="Nenhum recurso para este cliente" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Grupo</th>
                <th className="h-9 px-4 font-semibold">Serial</th>
                <th className="h-9 px-4 font-semibold">Garantia</th>
                <th className="h-9 px-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.items.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setDetailId(r.id)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setDetailId(r.id);
                  }}
                  className="h-10 cursor-pointer border-b border-divider transition-colors last:border-0 hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none"
                >
                  <td className="max-w-56 truncate px-4 font-medium" title={r.name}>
                    {r.name}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground">
                    {r.equipmentTypeName}
                  </td>
                  <td className="max-w-36 truncate px-4 text-muted-foreground">
                    {r.equipmentGroupName ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {r.serialNumber ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    <EquipmentWarranty equipment={r} />
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    <EquipmentStatusBadge active={r.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer ? (
        <ResourceDrawer
          initial={drawer.mode === 'edit' ? drawer.equipment : undefined}
          presetClient={{ id: clientId, name: clientName }}
          onClose={() => setDrawer(null)}
        />
      ) : null}
      {groupsOpen ? (
        <ResourceGroupsDialog
          clientId={clientId}
          clientName={clientName}
          onClose={() => setGroupsOpen(false)}
        />
      ) : null}
      {detailId !== null ? (
        <ResourceDetailDialog
          equipmentId={detailId}
          onEdit={(eq: EquipmentDetail) => {
            setDetailId(null);
            setDrawer({ mode: 'edit', equipment: eq });
          }}
          onClose={() => setDetailId(null)}
        />
      ) : null}
    </section>
  );
}
